import { LitElement, html, nothing } from 'lit';

import { ifDefined } from 'lit/directives/if-defined.js';
import { map } from 'lit/directives/map.js';
import { ref } from 'lit/directives/ref.js';
import { cache } from 'lit/directives/cache.js';
import { FormAssociatedRequiredMixin } from './form_associated_required_mixin';
import { requiredValidator } from '../utilities/validators';
import { EventEmitterMixin } from './event_emitter';

export default class FloatSelect extends FormAssociatedRequiredMixin(
  EventEmitterMixin(LitElement)
) {
  createRenderRoot() {
    return this;
  }

  static get properties() {
    return {
      options: { type: Array, attribute: true, state: true },
      value: { type: String, attribute: false },
      name: { type: String },
      id: { type: String },
      disabled: { type: Boolean },
      validateOnly: {
        type: Boolean,
        reflect: true,
        attribute: 'validate-only',
      },
      readOnly: { type: Boolean, reflect: true, attribute: 'readonly' },
      placeholder: { type: String, reflect: true },
      autofocus: { type: Boolean },
      tabIndex: { type: Number },
      hasInteracted: { type: Boolean, state: false, reflect: false },
      label: { type: String },
      autocomplete: { type: String },
    };
  }

  constructor() {
    super();
    this.tabIndex = 0;
    this.validateOnly = false;
    this.hasInteracted = false;
    this.validators = [
      {
        ...requiredValidator,
        isValid: () => requiredValidator.isValid(this),
      },
      {
        key: 'valueMissing',
        message: () => 'Please select an option',
        isValid: () => {
          if (!this.required) return true;

          const selectedOption = this.options?.find(
            (option) => option.value === this.select?.value
          );

          return (
            selectedOption &&
            !selectedOption.disabled &&
            !selectedOption.hidden &&
            selectedOption.value !== ''
          );
        },
      },
    ];
  }

  reportValidity() {
    this.updateValidity();
    this._dirty = true;
    this.setInvalidState();
    return this.validity.valid;
  }

  checkValidity() {
    this.updateValidity();
    return this.validity.valid;
  }

  updateValidity() {
    const validity = {};
    let validationMessage = '';

    const isValid = this.options?.some(
      (option) =>
        option.value === this.select?.value &&
        !option.disabled &&
        !option.hidden &&
        option.value !== ''
    );

    validity.valueMissing = this.required && !isValid;

    if (validity.valueMissing) {
      validationMessage = 'Please select an option';
    }

    this.__internals.setValidity(validity, validationMessage);
  }

  get value() {
    return this.select?.value || '';
  }

  set value(value) {
    if (this.select) {
      this.select.value = value ?? '';
    }
    this._value = value ?? '';
    if (!this.hasInteracted) return;
    this.setFormValue(value || null);
    this.updateValidity();
    this.setInvalidState();
  }

  firstUpdated() {
    if (this.isConnected) {
      setTimeout(() => {
        if (this.select) {
          this.value = this.select.value;
          if (this.select.value && this.select.value !== '') {
            this.hasInteracted = true;
            this.handleChange();
          }
        }
      }, 100);
    }
  }

  handleChange() {
    this.value = this.select.value;
    this._dirty = true;
    this.updateValidity();
    this.setInvalidState();
    this.emitEvent('change', { detail: this.value });
  }

  constraintsChanged() {
    this.updateValidity();
  }

  handleFocus() {
    this._dirty = true;
    this.emitEvent('focus');
  }

  handleBlur() {
    this.hasInteracted = true;
    this.value = this.select?.value;
    this._dirty = true;
    this.updateValidity();
    this.setInvalidState();
    this.emitEvent('blur');
  }

  renderValid() {
    return this.validationMessage
      ? html`
          <span class="float_field_error">
            <svg
              width="13"
              height="13"
              viewBox="0 0 29 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              title="Warning symbol" role="img" alt="Warning symbol"
              id=${`${this.id}_error_message`}
            >
              <path
                d="M27.723 19.2958L16.594 1.58862C15.9495 0.563405 15.0804 0 14.1436 0C13.2061 0 12.3366 0.563859 11.6923 1.58953L0.589826 19.2689C-0.0544873 20.2939 -0.175124 21.2728 0.248597 22.0282C0.675535 22.7884 1.59053 23.2059 2.82585 23.2073L25.4945 23.2244H25.4752V23.2266C26.5823 23.2266 27.6328 22.811 28.0593 22.0532C28.4831 21.3001 28.3673 20.3214 27.723 19.2958ZM14.7613 19.6921C14.5966 19.8546 14.3744 19.9469 14.1414 19.9469C13.9088 19.9469 13.6825 19.8537 13.5171 19.6912C13.353 19.5287 13.2599 19.3019 13.2602 19.0701C13.2606 18.8396 13.3151 18.6142 13.4796 18.453C13.6476 18.2889 13.79 18.1951 14.0115 18.1951L14.0529 18.1946C14.285 18.1946 14.5607 18.2889 14.7276 18.4539C14.8925 18.6162 15.0109 18.841 15.0104 19.0719C15.0102 19.3042 14.9275 19.5303 14.7613 19.6921ZM14.1441 16.4426C13.9075 16.4426 13.688 16.3517 13.5207 16.1862C13.3534 16.0206 13.2629 15.8003 13.2631 15.5658L13.1703 7.66426C13.1712 7.18062 13.5134 6.78835 13.9564 6.78835H14.0582C14.2951 6.78835 14.5683 6.87949 14.7354 7.04517C14.9029 7.21108 15.0187 7.43085 15.0185 7.66539L15.0242 15.5669C15.0237 16.0496 14.6329 16.4426 14.1441 16.4426Z"
                fill="currentColor"
              />
            </svg>
            ${this.validationMessage}
          </span>
        `
      : nothing;
  }

  renderSelect() {
    return html`
      <select
        ${ref((el) => (this.select = el))}
        id=${this.id}
        name=${this.name}
        aria-label=${ifDefined(this.label)}
        aria-placeholder=${ifDefined(this.placeholder)}
        aria-describedby=${`${this.id}_error_message`}
        autocomplete=${ifDefined(this.autocomplete)}
        ?required=${this.required}
        ?disabled=${this.disabled}
        ?readonly=${this.readOnly}
        ?autofocus=${this.autofocus}
        tabindex=${this.tabIndex}
        @change=${this.handleChange}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      >
        ${cache(
      map(
        this.options,
        (option) => html` <option
              value="${ifDefined(option.value)}"
              ?disabled=${option.disabled}
              ?hidden=${option.hidden}
              ?selected=${option.selected}
              ?multiple=${option.multiple}
              ?required=${option.required}
              props="${ifDefined(JSON.stringify(option.props))}"
            >
              ${option.label}
            </option>`
      )
    )}
      </select>
    `;
  }

  render() {
    if (!this.options || !this.options.length) return nothing;

    return html`${this.renderSelect()}
    ${this.invalid ? this.renderValid() : nothing}`;
  }
}
window.customElements.get('float-select') ||
  window.customElements.define('float-select', FloatSelect);
